// src/storage/useProgress.js
//
// Auth-bound hooks for PERSISTED study progress (Scope A) -- the cross-device
// replacement for the orphaned localStorage completedTexts/quizScores. Owner
// client-writes to /users/{uid}/progress/{packId}, mirroring useUserContent
// (onSnapshot, serverTimestamp(), no-op when signed-out / mock / no-db). All writes
// MUST use serverTimestamp() for updatedAt (rules require updatedAt == request.time).
//
// Scope A ONLY: completion + quiz scores. NO essay / free-text (Scope B is separate).
import { teachersPet } from '../lib/callables.js';
import { PET_ENABLED } from '../teachersPet/config.js';
import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, doc, onSnapshot, setDoc, serverTimestamp, arrayUnion, arrayRemove } from 'firebase/firestore';
import { observeBetaOperation } from '../analytics/betaOperations.js';
import { db, AUTH_MODE } from '../auth/firebase.js';
import { useAuth } from '../auth/useAuth.js';
import { aggregateProgress } from './progressHelpers.js';

const disabled = () => AUTH_MODE === 'mock' || !db;

// Per-pack progress: the live completion set + quiz scores, plus the writers.
export function useProgress(packId, { articleRewards = false } = {}) {
    const { currentUser } = useAuth();
    const uid = currentUser?.uid || null;
    const enabled = !disabled() && !!uid && !!packId;
    const [completionMessage, setCompletionMessage] = useState('');
    const pending = useRef(new Set()), generation = useRef(0);
    const [completedItemIds, setCompletedItemIds] = useState([]);
    const [quizScores, setQuizScores] = useState({});
    const [quizState, setQuizState] = useState({}); // key -> { data:<json>, updatedAt } (resumable quiz blob)

    useEffect(() => {
        generation.current++; setCompletionMessage('');
        if (!enabled) { setCompletedItemIds([]); setQuizScores({}); setQuizState({}); return undefined; }
        const ref = doc(db, 'users', uid, 'progress', packId);
        const unsub = onSnapshot(
            ref,
            (snap) => {
                const d = snap.exists() ? snap.data() : {};
                setCompletedItemIds(Array.isArray(d.completedItemIds) ? d.completedItemIds : []);
                setQuizScores(d.quizScores && typeof d.quizScores === 'object' ? d.quizScores : {});
                setQuizState(d.quizState && typeof d.quizState === 'object' ? d.quizState : {});
            },
            (err) => { console.error('useProgress listener error:', err); setCompletedItemIds([]); setQuizScores({}); setQuizState({}); },
        );
        return () => unsub();
    }, [uid, packId, enabled]);

    const completedSet = new Set(completedItemIds);

    // Manual tick (reader-manual-progress): mark an item complete. Idempotent --
    // skips the write if already complete. arrayUnion keeps it safe across races.
    const markItemComplete = useCallback(async (itemId) => {
        if (!enabled || !itemId || completedItemIds.includes(itemId)) return;
        const key = uid + ':' + packId + ':' + itemId, version = generation.current;
        if (pending.current.has(key)) return;
        pending.current.add(key); setCompletionMessage('');
        try {
            if (articleRewards && PET_ENABLED) {
                const result = await teachersPet({ action: 'complete-article', packId, itemId });
                if (version === generation.current) setCompletionMessage(result.award ? '+' + result.award + ' coins for your first completion. Added to Teacher’s Pet.' : result.skin ? 'Unlocked: ' + result.skin.title + '. Find it in Teacher’s Pet.' : 'Progress saved.');
                return result;
            }
            await setDoc(doc(db, 'users', uid, 'progress', packId),
                { completedItemIds: arrayUnion(itemId), updatedAt: serverTimestamp() }, { merge: true });
            return { completed: true };
        } catch (e) {
            console.error('markItemComplete write error:', e);
            if (version === generation.current) setCompletionMessage('Could not save completion. Please try Mark complete again.');
            return null;
        } finally { pending.current.delete(key); }
    }, [enabled, uid, packId, completedItemIds, articleRewards]);

    // Manual un-tick (reader-manual-progress): remove an item from the completed
    // set. Idempotent -- skips the write if not currently complete. Mirrors
    // markItemComplete (same doc + serverTimestamp merge); arrayRemove is accepted
    // by validProgress (result is still a list <= 500 -- no rules change).
    const unmarkItemComplete = useCallback(async (itemId) => {
        if (!enabled || !itemId || !completedItemIds.includes(itemId)) return;
        try {
            // Record pre-existing completion before its tick is removed.
            if (articleRewards && PET_ENABLED) await teachersPet({ action: 'complete-article', packId, itemId });
            await setDoc(doc(db, 'users', uid, 'progress', packId),
                { completedItemIds: arrayRemove(itemId), updatedAt: serverTimestamp() }, { merge: true });
        } catch (e) { console.error('unmarkItemComplete write error:', e); setCompletionMessage('Could not update completion. Please try again.'); }
    }, [enabled, uid, packId, completedItemIds, articleRewards]);

    // Records the LATEST quiz run under `key` (one pooled quiz per pack -> POOLED_QUIZ_KEY).
    const recordQuizRun = useCallback(async (key, { score, total, answered = total } = {}) => {
        if (!enabled || !key) return;
        try {
            await observeBetaOperation('quiz_save', { packId }, () => setDoc(doc(db, 'users', uid, 'progress', packId),
                { quizScores: { [key]: { score: Number(score) || 0, total: Number(total) || 0, answered: Number(answered) || 0, takenAt: serverTimestamp() } }, updatedAt: serverTimestamp() },
                { merge: true }));
        } catch (e) { console.error('recordQuizRun write error:', e); throw e; }
    }, [enabled, uid, packId]);

    // Manually SAVE a resumable quiz session under `key` (POOLED_QUIZ_KEY). Mirrors
    // recordQuizRun (merge:true + serverTimestamp). The { answers, submitted } blob is
    // stored as a JSON STRING under `data` -- a scalar so a save-after-reset OVERWRITES
    // cleanly (last-write-wins). If it were a nested map, setDoc's deep merge would keep
    // stale answer/submitted keys. Score is derivable and NOT stored; no scroll position.
    const saveQuizState = useCallback(async (key, { answers, submitted } = {}) => {
        if (!enabled || !key) return;
        try {
            await setDoc(doc(db, 'users', uid, 'progress', packId),
                { quizState: { [key]: { data: JSON.stringify({ answers: answers || {}, submitted: submitted || {} }), updatedAt: serverTimestamp() } }, updatedAt: serverTimestamp() },
                { merge: true });
        } catch (e) { console.error('saveQuizState write error:', e); }
    }, [enabled, uid, packId]);

    return { completionMessage, completedItemIds, completedSet, quizScores, quizState, markItemComplete, unmarkItemComplete, recordQuizRun, saveQuizState, enabled };
}

// Aggregate progress across ALL the signed-in user's packs (dashboard stats).
export function useAllProgress() {
    const { currentUser } = useAuth();
    const uid = currentUser?.uid || null;
    const [docs, setDocs] = useState([]);

    useEffect(() => {
        if (disabled() || !uid) { setDocs([]); return undefined; }
        const ref = collection(db, 'users', uid, 'progress');
        const unsub = onSnapshot(
            ref,
            (snap) => { const list = []; snap.forEach((d) => list.push(d.data())); setDocs(list); },
            (err) => { console.error('useAllProgress listener error:', err); setDocs([]); },
        );
        return () => unsub();
    }, [uid]);

    return aggregateProgress(docs);
}
